import type {
  EventSpan,
  TimeGridEvent,
  TimeRange,
  TimeSlot,
  EventLayout,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

export const isValidEventRange = (event: TimeGridEvent): boolean => {
  return event.end > event.start
}

export const clampEventRange = (event: TimeGridEvent, timeRange: TimeRange): TimeRange => {
  const start = event.start < timeRange.start ? timeRange.start : event.start
  const end = event.end > timeRange.end ? timeRange.end : event.end

  return { start, end }
}

export const intersectsSlot = (range: TimeRange, slot: TimeSlot): boolean => {
  return range.start < slot.end && range.end > slot.start
}

// Returns the inclusive slot range intersecting the event (end-exclusive semantics).
export const computeEventSpan = (range: TimeRange, slots: TimeSlot[]): EventSpan | null => {
  const indices = slots.filter((slot) => intersectsSlot(range, slot)).map((slot) => slot.index)

  if (indices.length === 0) return null

  return {
    startIndex: indices[0] ?? 0,
    endIndex: indices[indices.length - 1] ?? 0,
  }
}

// Builds clamped spans for each event within the visible slots.
export const buildEventLayouts = <TEvent extends TimeGridEvent>({
  events,
  slots,
  timeRange,
}: {
  events: TEvent[]
  slots: TimeSlot[]
  timeRange: { start: Date; end: Date }
}): EventLayout<TEvent>[] => {
  return events
    .filter(isValidEventRange)
    .map((event) => {
      const clampedRange = clampEventRange(event, timeRange)
      const span = computeEventSpan(clampedRange, slots)

      if (!span) return null

      return { event, span, stackIndex: 0, clampedRange }
    })
    .filter((layout): layout is EventLayout<TEvent> => Boolean(layout))
}
